import { useTranslation } from 'react-i18next';
import GlassCard from '../ui/GlassCard';
import { Profile, Address } from '../../lib/api';
import { formatDisplay } from '../../lib/phone';

interface FormData {
  firstName: string;
  lastName: string;
  title: string;
  tagline: string;
  phone: string;
  email: string;
  website: string;
  accentColor: string;
  address: Address;
}

interface LivePreviewCardProps {
  formData: FormData;
  profile: Profile | null;
}

export default function LivePreviewCard({ formData, profile }: LivePreviewCardProps) {
  const { t } = useTranslation();
  const accentColor = formData.accentColor || '#e91e8c';

  return (
    <GlassCard className="p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        {t('dashboard.preview.title')}
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        {t('dashboard.preview.howOthersSee')}
      </p>

      {/* Phone mockup */}
      <div className="flex justify-center">
        <div className="w-56">
          {/* Phone frame */}
          <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
            {/* Screen */}
            <div className="bg-white rounded-[1.5rem] overflow-hidden">
              {/* Status bar */}
              <div className="h-5 bg-gray-100 flex items-center justify-center">
                <div className="w-16 h-3 bg-gray-900 rounded-full" />
              </div>

              {/* Profile content */}
              <div className="p-3">
                {/* Accent bar */}
                <div
                  className="h-6 rounded-t-lg"
                  style={{ backgroundColor: accentColor }}
                />

                {/* Card content */}
                <div className="bg-white shadow-lg rounded-b-lg p-3 -mt-3 pt-6 text-center border border-gray-100">
                  {/* Avatar */}
                  <div
                    className="w-12 h-12 mx-auto -mt-9 mb-2 rounded-full flex items-center justify-center text-white text-sm font-bold border-2 border-white shadow-lg overflow-hidden"
                    style={{ backgroundColor: accentColor }}
                  >
                    {profile?.photoUrl ? (
                      <img
                        key={profile.photoUrl}
                        src={profile.photoUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <>
                        {formData.firstName?.[0] || 'J'}
                        {formData.lastName?.[0] || 'D'}
                      </>
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="font-bold text-gray-900 text-xs">
                    {formData.firstName || 'First'} {formData.lastName || 'Last'}
                  </h3>

                  {/* Tagline */}
                  {formData.tagline && (
                    <p className="text-gray-500 text-[10px] italic mt-0.5">
                      "{formData.tagline}"
                    </p>
                  )}

                  {/* Title */}
                  <p className="text-gray-500 text-[10px] mt-0.5">
                    {formData.title || 'Title'}
                  </p>

                  {/* Contact preview items */}
                  <div className="mt-2 space-y-1">
                    {formData.phone && (
                      <div className="flex items-center gap-1.5 text-[9px] text-gray-600">
                        <div
                          className="w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: accentColor }}
                        >
                          <PhoneIcon className="w-2 h-2 text-white" />
                        </div>
                        <span className="truncate">{formatDisplay(formData.phone)}</span>
                      </div>
                    )}
                    {formData.email && (
                      <div className="flex items-center gap-1.5 text-[9px] text-gray-600">
                        <div
                          className="w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: accentColor }}
                        >
                          <EmailIcon className="w-2 h-2 text-white" />
                        </div>
                        <span className="truncate">{formData.email}</span>
                      </div>
                    )}
                  </div>

                  {/* CTA button preview */}
                  <div
                    className="mt-2 py-1.5 rounded-full text-white text-[9px] font-medium"
                    style={{ backgroundColor: accentColor }}
                  >
                    {t('profile.showContact')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View live link */}
      {profile?.publicId && (
        <div className="mt-4 text-center">
          <a
            href={`${window.location.origin}/p/${profile.publicId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-mk-pink-600 hover:text-mk-pink-700 hover:underline"
          >
            {t('dashboard.preview.viewProfile')} →
          </a>
        </div>
      )}
    </GlassCard>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function EmailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}
